import {
  Select as GSSelect,
  SelectInput,
  FormControl,
  SelectTrigger,
  View
} from "@gluestack-ui/themed";
import { SelectHelperText } from "./components/SelectHelperText";
import { SelectError } from "./components/SelectError";
import { SelectIcon } from "./components/SelectIcon";
import { SelectLabel } from "./components/SelectLabel";

import { SelectProps, ISelectOption } from "./types";
import { SelectList } from "@repo/core/components/core/Select/components/SelectList";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectListRef } from "@repo/core/components/core/Select/components/SelectList/types";
import { ListOptionsSelected } from "@repo/core/components/core/Select/components/ListOptionsSelected";

/** Cooldown in ms to prevent modal from reopening when touch propagates after selection */
const SELECT_CLOSE_COOLDOWN_MS = 400;

export const Select = ({
  label,
  isLoading,
  placeholder,
  helperText,
  errorMessage,
  options,
  value,
  isMultiple,
  isSearchable = true,
  isDisabled,
  isInvalid,
  isReadOnly,
  isRequired,
  light,
  onChange,
  ...selectProps
}: SelectProps) => {
  const listRef = useRef<SelectListRef>(null);
  const lastSelectionTimeRef = useRef<number>(0);
  const [currentValue, setCurrentValue] = useState(value);

  const handleOpenList = useCallback(() => {
    const now = Date.now();
    if (now - lastSelectionTimeRef.current < SELECT_CLOSE_COOLDOWN_MS) {
      return;
    }
    listRef.current?.open();
  }, []);

  const customPlaceHolder = useMemo(() => {
    if (currentValue) {
      if (isMultiple) {
        if (currentValue.length) {
          return `${currentValue.length} selecionado${currentValue.length > 1 ? "s" : ""}`;
        }

        return placeholder;
      }

      const itemName = options.find(
        (option) => option.value === currentValue
      )?.label;
      return itemName;
    }
    return placeholder;
  }, [currentValue, isMultiple, options, placeholder]);

  const itemsSelecteds = useMemo(() => {
    if (isMultiple && currentValue?.length) {
      const selecteds = options.filter((option) =>
        currentValue.includes(option.value)
      );
      return selecteds.map((option) => option.label);
    }

    return null;
  }, [currentValue, isMultiple, options]);

  const handleOnChange = useCallback(
    (newValue: string | string[]) => {
      lastSelectionTimeRef.current = Date.now();
      setCurrentValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  const placeholderColorText = useMemo(() => {
    const hasValue = isMultiple
      ? Array.isArray(currentValue) && currentValue.length > 0
      : currentValue && currentValue !== "";

    return hasValue ? "$textDark800" : "$textLight500";
  }, [isMultiple, currentValue]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  return (
    <>
      <View>
        <FormControl
          isDisabled={isDisabled || isLoading}
          isInvalid={isInvalid || !!errorMessage}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
          flex={selectProps.flex}
          width={selectProps.width}
        >
          <SelectLabel label={label} light={light} />
          <GSSelect {...selectProps} borderRadius="$lg">
            <SelectTrigger
              size="xl"
              onPress={handleOpenList}
              bg="$backgroundLight100"
              borderRadius="$lg"
              borderColor="$backgroundDark100"
              borderBlockColor="$backgroundDark100"
            >
              <SelectInput
                bg="$backgroundLight100"
                placeholderTextColor={placeholderColorText}
                placeholder={customPlaceHolder}
                fontSize={16}
              />
              <SelectIcon name="chevron-down" size="xs" mt="$2" />
            </SelectTrigger>
          </GSSelect>
          <SelectHelperText text={helperText} hasError={!!errorMessage} />
          <SelectError errorMessage={errorMessage} />
        </FormControl>
        <ListOptionsSelected itemsSelecteds={itemsSelecteds} />
      </View>
      <SelectList
        ref={listRef}
        items={options}
        isMultiple={isMultiple}
        isSearchable={isSearchable}
        value={currentValue}
        onChange={handleOnChange}
        onCancel={() => {
          lastSelectionTimeRef.current = Date.now();
          listRef.current?.close();
        }}
      />
    </>
  );
};

export type { ISelectOption };
